// Giao dịch: nhãn trạng thái, giá trị sau chiết khấu, lịch thanh toán (đợt đầu trừ cọc), số đã thanh toán, đợt kế tiếp.
import { DAY } from './dates.js';
import { DEFAULT_PLAN } from './inventory.js';
import type { Deal, DealStatus, Money, Project, Timestamp } from './types.js';

export type DealTone = 'violet' | 'warn' | 'good' | 'bad';
export const DEAL_STATUS: Readonly<Record<DealStatus, { label: string; tone: DealTone }>> = {
  pending: { label: 'Chờ duyệt cọc', tone: 'violet' },
  deposited: { label: 'Đã cọc', tone: 'warn' },
  contracted: { label: 'Đã ký HĐ', tone: 'good' },
  completed: { label: 'Hoàn tất', tone: 'good' },
  rejected: { label: 'Bị từ chối', tone: 'bad' },
  cancelled: { label: 'Đã huỷ', tone: 'bad' },
};
/** Giao dịch "sống": đã xác nhận cọc và chưa huỷ — tính doanh số, hoa hồng. */
export const LIVE_DEAL: readonly DealStatus[] = ['deposited', 'contracted', 'completed'];
export const PAY_METHODS: readonly string[] = ['Chuyển khoản', 'Tiền mặt tại CĐT', 'Vay ngân hàng giải ngân', 'Khác'];

/** Giá trị giao dịch = round(giá niêm yết × (1 − ck%) − ck tiền), không âm. */
export function dealValue(listPrice: Money, discountPct: number | null | undefined = 0, discountAmt: Money | null | undefined = 0): Money {
  return Math.max(0, Math.round(listPrice * (1 - (discountPct || 0) / 100) - (discountAmt || 0)));
}

export interface ScheduleItem {
  label: string;
  pct: number;
  amount: Money;
  due: Timestamp;
}
export type ScheduleDeal = Pick<Deal, 'value' | 'createdAt'> & Partial<Pick<Deal, 'confirmedAt' | 'deposit'>>;
export type PlanProject = Partial<Pick<Project, 'paymentPlan'>>;

/**
 * Lịch thanh toán theo tiến độ dự án (thiếu ⇒ DEFAULT_PLAN): mỗi đợt round(value × pct%), đợt đầu trừ tiền cọc
 * (không âm); hạn = (confirmedAt || createdAt) + days ngày.
 */
export function buildSchedule(deal: ScheduleDeal, project: PlanProject | null | undefined): ScheduleItem[] {
  const plan = project && project.paymentPlan && project.paymentPlan.length ? project.paymentPlan : DEFAULT_PLAN;
  const start = deal.confirmedAt || deal.createdAt;
  const carry = deal.deposit || 0;
  return plan.map((p, i) => {
    let amount = Math.round((deal.value * p.pct) / 100);
    if (i === 0) amount = Math.max(0, amount - carry);
    return { label: p.label, pct: p.pct, amount, due: start + (p.days || 0) * DAY };
  });
}

export type PaidDeal = Pick<Deal, 'status'> & Partial<Pick<Deal, 'confirmedAt' | 'deposit' | 'payments'>>;

/** Đã thanh toán = tiền cọc (khi đã xác nhận cọc, kể cả đã huỷ) + tổng các lần thanh toán. */
export function paidTotal(deal: PaidDeal): Money {
  const dep = ['deposited', 'contracted', 'completed', 'cancelled'].includes(deal.status) || deal.confirmedAt ? deal.deposit || 0 : 0;
  return dep + (deal.payments || []).reduce((s, p) => s + (p.amount || 0), 0);
}
/** Tỷ lệ đã thanh toán trên giá trị (0 khi value = 0). */
export const paidRatio = (deal: PaidDeal & Pick<Deal, 'value'>) => (deal.value ? paidTotal(deal) / deal.value : 0);

export interface Installment extends ScheduleItem {
  /** Số còn thiếu để đủ luỹ kế tới hết đợt này. */
  remaining: Money;
}
/** Đợt kế tiếp chưa thanh toán đủ (theo luỹ kế cọc + các đợt); null = đã đủ. */
export function nextInstallment(deal: ScheduleDeal & PaidDeal, project: PlanProject | null | undefined): Installment | null {
  const sch = buildSchedule(deal, project);
  let cum = deal.deposit || 0;
  const paid = paidTotal(deal);
  for (const s of sch) {
    cum += s.amount;
    if (paid < cum) return { ...s, remaining: cum - paid };
  }
  return null;
}
